import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Badge, Spinner } from 'react-bootstrap';
import { FaDownload, FaCalendarAlt, FaUserMd, FaChartBar, } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import AttendanceStrategyFactory from '../services/attendance/AttendanceStrategyFactory';
import '../styles/DoctorAttendanceSummery.css';

const PoojariAttendanceSummery = () => {
     const [employeeData, setEmployeeData] = useState([]);
     const [summary, setSummary] = useState({});
     const [loading, setLoading] = useState(false);
     const [selectedMonth, setSelectedMonth] = useState('2025-11');
     const [selectedToMonth, setSelectedToMonth] = useState('2025-11');
     const [currentPage, setCurrentPage] = useState(1);
     const [searchTerm, setSearchTerm] = useState('');
     const recordsPerPage = 50;

     const BASE_URL = process.env.REACT_APP_BASE_URL;
     const token = sessionStorage.getItem('token');

     // eslint-disable-next-line react-hooks/exhaustive-deps


     const fetchpoojariAttendance = async () => {
          setLoading(true);
          try {
               const response = await fetch(`${BASE_URL}/all-poojari-employee-attendance`, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                         'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                         selectedMonth,
                         selectedToMonth,
                         emp_type: 'POOJARI'
                    })
               });

               if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
               }

               const result = await response.json();
               console.log('Employee Attendance Response:', result);

               if (result.status && result.data) {
                    setEmployeeData(result.data);
                    setSummary(result.summary || {});
               } else {
                    setEmployeeData([]);
                    setSummary({});
               }
          } catch (error) {
               console.error('Error fetching employee attendance:', error);
               setEmployeeData([]);
               setSummary({});
          } finally {
               setLoading(false);
          }
     };
     useEffect(() => {
          if (selectedMonth && selectedToMonth) {
               fetchpoojariAttendance();
          }
     }, [selectedMonth, selectedToMonth]);

     // ✅ NEW: Initialize poojari Strategy for calculations
     const getEmployeeStrategy = () => {
          try {
               return AttendanceStrategyFactory.getStrategy('CONTRACTUAL_EMPLOYEE');
          } catch (error) {
               console.error('Error getting employee strategy:', error);
               return null;
          }
     };

     // ✅ UPDATED: Calculate poojari stats using strategy
     const calculatepoojariStats = (poojari) => {
          const attendance = poojari.attendance || {};
          const days = attendance.days || [];
          const dutyRoster = attendance.duty_roster || [];
          const holidays = attendance.holidays || [];
          const strategy = getEmployeeStrategy();

          let totalDays = days.length;
          let presentDays = 0;
          let totalActualWorkingSeconds = 0;
          let totalConsideredWorkingSeconds = 0;

          days.forEach(day => {
               const roster = dutyRoster.find(r => r.attendance_date === day.date);

               if (day.punch_in && day.punch_out) {
                    presentDays++;

                    // Calculate actual worked hours (raw punch difference)
                    const punchIn = new Date(day.punch_in);
                    const punchOut = new Date(day.punch_out);
                    const actualSeconds = Math.floor((punchOut - punchIn) / 1000);
                    totalActualWorkingSeconds += actualSeconds;

                    // Calculate considered worked hours using strategy
                    if (strategy) {
                         const employeeData = {
                              weekAttendance: attendance.weekAttendance || [],
                              allDays: days,
                              region: attendance.employee_region || 'Other'
                         };

                         const { workingHours } = strategy.calculateAttendanceStatus(day, roster, holidays, employeeData);

                         // Parse working hours back to seconds
                         if (workingHours && workingHours !== '-') {
                              const [hours, minutes, seconds] = workingHours.split(':').map(Number);
                              const consideredSeconds = (hours * 3600) + (minutes * 60) + (seconds || 0);
                              totalConsideredWorkingSeconds += consideredSeconds;
                         }
                    } else {
                         // Fallback: use actual worked seconds
                         totalConsideredWorkingSeconds += actualSeconds;
                    }
               }
          });

          const absentDays = totalDays - presentDays;
          const attendancePercentage = totalDays > 0 ? ((presentDays / totalDays) * 100).toFixed(1) : '0.0';

          return {
               totalDays,
               presentDays,
               absentDays,
               attendancePercentage,
               totalActualWorkingHours: (totalActualWorkingSeconds / 3600).toFixed(1),
               totalConsideredWorkingHours: (totalConsideredWorkingSeconds / 3600).toFixed(1)
          };
     };

     const filteredpoojaris = employeeData.filter(poojari =>
          poojari.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          poojari.employee_code.toLowerCase().includes(searchTerm.toLowerCase())
     );

     const paginatedpoojaris = filteredpoojaris.slice(
          (currentPage - 1) * recordsPerPage,
          currentPage * recordsPerPage
     );

     const totalPages = Math.ceil(filteredpoojaris.length / recordsPerPage);

     // ✅ UPDATED: Enhanced exportToExcel with strategy integration
     const exportToExcel = () => {
          if (employeeData.length === 0) return;

          const strategy = getEmployeeStrategy();
          console.log('Using strategy for Excel export:', strategy?.constructor?.name);

          // Create summary sheet data
          const summaryData = [{
               'Report Type': 'Poojari Attendance Summary',
               'Period': `${selectedMonth} to ${selectedToMonth}`,
               'Total Poojaris': summary.total_employees || 0,
               'Poojaris with Attendance': summary.employees_with_attendance || 0,
               'Total Attendance Records': summary.total_attendance_records || 0,
               'Generated On': new Date().toLocaleString(),
               'Strategy Used': strategy?.constructor?.name || 'Default'
          }];

          // Create detailed summary data
          const detailedData = employeeData.map(poojari => {
               const stats = calculatepoojariStats(poojari);
               // const attendance = poojari.attendance || {};

               return {
                    'Employee Code': poojari.employee_code,
                    'Employee Name': poojari.employee_name,
                    'Employee Type': poojari.emp_type,
                    'Total Days': stats.totalDays,
                    'Present Days': stats.presentDays,
                    'Absent Days': stats.absentDays,
                    'Attendance %': stats.attendancePercentage + '%',
                    'Total Actual Working Hours': stats.totalActualWorkingHours,
                    'Total Considered Working Hours': stats.totalConsideredWorkingHours,

               };
          });


          // ✅ UPDATED: Create comprehensive daily attendance with strategy calculations
          const allpoojarisDailyData = [];

          employeeData.forEach((poojari) => {
               const attendance = poojari.attendance || {};
               const days = attendance.days || [];
               const dutyRoster = attendance.duty_roster || [];
               const holidays = attendance.holidays || [];

               if (days.length > 0) {
                    days.forEach(day => {
                         const date = new Date(day.date);

                         // Find matching duty roster entry for this date
                         const rosterEntry = dutyRoster.find(roster =>
                              roster.attendance_date === day.date
                         );

                         // Extract shift hours from duty roster (total_hours field)
                         let shiftHours = 0;
                         if (rosterEntry && rosterEntry.total_hours) {
                              const [hours, minutes] = rosterEntry.total_hours.split(':').map(Number);
                              shiftHours = hours + (minutes / 60);
                         } else if (day.shift_total_minutes) {
                              shiftHours = day.shift_total_minutes / 60;
                         }

                         // Calculate actual worked hours (raw punch difference)
                         let actualWorkedHours = '0.00';
                         let actualWorkedSeconds = 0;
                         if (day.punch_in && day.punch_out) {
                              const punchIn = new Date(day.punch_in);
                              const punchOut = new Date(day.punch_out);
                              actualWorkedSeconds = Math.floor((punchOut - punchIn) / 1000);
                              actualWorkedHours = (actualWorkedSeconds / 3600).toFixed(2);
                         }

                         // ✅ NEW: Calculate considered worked hours using strategy
                         let consideredWorkedHours = '0.00';
                         let strategyStatus = 'Unknown';
                         let strategyRemarks = '';

                         if (strategy) {
                              const employeeData = {
                                   weekAttendance: attendance.weekAttendance || [],
                                   allDays: days,
                                   region: attendance.employee_region || 'Other'
                              };

                              const strategyResult = strategy.calculateAttendanceStatus(day, rosterEntry, holidays, employeeData);
                              strategyStatus = strategyResult.status;
                              strategyRemarks = strategyResult.remarks || '';

                              // Parse working hours from strategy
                              if (strategyResult.workingHours && strategyResult.workingHours !== '-') {
                                   const [hours, minutes, seconds] = strategyResult.workingHours.split(':').map(Number);
                                   const totalSeconds = (hours * 3600) + (minutes * 60) + (seconds || 0);
                                   consideredWorkedHours = (totalSeconds / 3600).toFixed(2);
                              }
                         } else {
                              // Fallback: use actual worked hours
                              consideredWorkedHours = actualWorkedHours;
                              strategyStatus = day.punch_in && day.punch_out ? 'Present' : 'Absent';
                         }

                         // ✅ UPDATED: Add comprehensive data to Excel
                         allpoojarisDailyData.push({
                              'Employee Code': poojari.employee_code,
                              'Employee Name': poojari.employee_name,
                              'Date': date.toLocaleDateString('en-IN', {
                                   year: 'numeric',
                                   month: '2-digit',
                                   day: '2-digit'
                              }),
                              'Day': date.toLocaleDateString('en-US', { weekday: 'short' }),
                              'Punch In': day.punch_in ?
                                   new Date(day.punch_in).toLocaleTimeString('en-IN', {
                                        hour12: false,
                                        hour: '2-digit',
                                        minute: '2-digit'
                                   }) : 'N/A',
                              'Punch Out': day.punch_out ?
                                   new Date(day.punch_out).toLocaleTimeString('en-IN', {
                                        hour12: false,
                                        hour: '2-digit',
                                        minute: '2-digit'
                                   }) : 'N/A',
                              'Actual Worked Hours': actualWorkedHours, // ✅ NEW: Raw punch difference
                              'Considered Worked Hours': consideredWorkedHours, // ✅ NEW: Strategy-calculated hours
                              'Status': strategyStatus, // ✅ UPDATED: Strategy status
                              'Shift Name': rosterEntry?.shift_name || 'N/A',
                              'Shift Hours': shiftHours.toFixed(2),
                              'Shift In Time': rosterEntry?.in_time || 'N/A',
                              'Shift Out Time': rosterEntry?.out_time || 'N/A',
                              'Shortfall Hours': day.shortfall_hours || '00:00:00',
                              'Has Shortfall': day.has_shortfall ? 'Yes' : 'No',
                              'Grace In (Minutes)': rosterEntry?.grace_in || 'N/A',
                              'Grace Out (Minutes)': rosterEntry?.grace_out || 'N/A',
                              'Strategy Remarks': strategyRemarks, // ✅ NEW: Strategy remarks
                              'Shift Type': rosterEntry?.shift_name?.startsWith('OP') ? 'Open Shift' : 'Regular Shift', // ✅ NEW: Shift type
                              'Week Off': rosterEntry?.custom_weekoff_date ? 'Yes' : 'No'
                         });
                    });
               }
          });

          // Create the workbook with enhanced sheets
          const workbook = XLSX.utils.book_new();

          // Sheet 1: Summary
          const summaryWs = XLSX.utils.json_to_sheet(summaryData);
          XLSX.utils.book_append_sheet(workbook, summaryWs, 'Summary');

          // Sheet 2: poojari Summary (overview of all poojaris)
          const detailedWs = XLSX.utils.json_to_sheet(detailedData);
          XLSX.utils.book_append_sheet(workbook, detailedWs, 'poojari Summary');

          // ✅ Sheet 3: ALL poojaris Daily Attendance with strategy calculations
          console.log(`Creating comprehensive sheet with ${allpoojarisDailyData.length} attendance records for ${employeeData.length} poojaris`);

          if (allpoojarisDailyData.length > 0) {
               const allDailyWs = XLSX.utils.json_to_sheet(allpoojarisDailyData);

               // Add auto-filter to make data searchable and filterable
               const range = XLSX.utils.encode_range({
                    s: { c: 0, r: 0 },
                    e: { c: 18, r: allpoojarisDailyData.length } // Updated column count
               });
               allDailyWs['!autofilter'] = { ref: range };

               // ✅ UPDATED: Enhanced column widths for new fields
               allDailyWs['!cols'] = [
                    { width: 15 }, // Employee Code
                    { width: 25 }, // Employee Name  
                    { width: 12 }, // Date
                    { width: 8 },  // Day
                    { width: 10 }, // Punch In
                    { width: 10 }, // Punch Out
                    { width: 18 }, // Actual Worked Hours
                    { width: 20 }, // Considered Worked Hours
                    { width: 12 }, // Status
                    { width: 12 }, // Shift Name
                    { width: 12 }, // Shift Hours
                    { width: 12 }, // Shift In Time
                    { width: 12 }, // Shift Out Time
                    { width: 15 }, // Shortfall Hours
                    { width: 12 }, // Has Shortfall
                    { width: 15 }, // Grace In
                    { width: 15 }, // Grace Out
                    { width: 20 }, // Strategy Remarks
                    { width: 15 }, // Shift Type
                    { width: 12 }  // Week Off
               ];

               XLSX.utils.book_append_sheet(workbook, allDailyWs, 'All poojaris Daily Attendance');
          }

          // Generate filename
          const filename = `poojari_Attendance_Summary_${selectedMonth}_to_${selectedToMonth}_${new Date().toISOString().split('T')[0]}.xlsx`;

          // Save file
          XLSX.writeFile(workbook, filename);

          console.log(`Excel export completed: ${filename}`);
          console.log(`Total poojaris: ${employeeData.length}`);
          console.log(`Total daily records: ${allpoojarisDailyData.length}`);
          console.log(`Strategy used: ${strategy?.constructor?.name || 'Default'}`);
     };

     const getAttendanceColor = (percentage) => {
          if (percentage >= 90) return 'success';
          if (percentage >= 75) return 'warning';
          return 'danger';
     };


     return (
          <Container fluid className="poojari-attendance-summary py-2">
               {/* Compact Summary Cards - Single Row */}
               <Row className="mb-2">
                    <Col md={3}>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center">
                                   <FaUserMd size={20} className="text-primary me-2" />
                                   <div>
                                        <h5 className="mb-0">{summary.total_employees || 0}</h5>
                                        <small className="text-muted">Total Poojaris</small>
                                   </div>
                              </Card.Body>
                         </Card>
                    </Col>
                    <Col md={3}>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center">
                                   <FaChartBar size={20} className="text-success me-2" />
                                   <div>
                                        <h5 className="mb-0">{summary.employees_with_attendance || 0}</h5>
                                        <small className="text-muted">With Attendance</small>
                                   </div>
                              </Card.Body>
                         </Card>
                    </Col>
                    <Col md={3}>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center">
                                   <FaCalendarAlt size={20} className="text-info me-2" />
                                   <div>
                                        <h5 className="mb-0">{summary.total_attendance_records || 0}</h5>
                                        <small className="text-muted">Total Records</small>
                                   </div>
                              </Card.Body>
                         </Card>
                    </Col>
                    <Col md={3}>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center justify-content-center">
                                   <Button
                                        variant="success"
                                        size="sm"
                                        onClick={exportToExcel}
                                        disabled={loading || employeeData.length === 0}
                                        className="d-flex align-items-center"
                                   >
                                        <FaDownload className="me-1" />
                                        Export Excel
                                   </Button>
                              </Card.Body>
                         </Card>
                    </Col>
               </Row>

               {/* Compact Filters */}
               <Row className="mb-2">
                    <Col md={2}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">From Month</Form.Label>
                              <Form.Control
                                   type="month"
                                   value={selectedMonth}
                                   onChange={(e) => setSelectedMonth(e.target.value)}
                                   size="sm"
                              />
                         </Form.Group>
                    </Col>
                    <Col md={2}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">To Month</Form.Label>
                              <Form.Control
                                   type="month"
                                   value={selectedToMonth}
                                   onChange={(e) => setSelectedToMonth(e.target.value)}
                                   size="sm"
                              />
                         </Form.Group>
                    </Col>
                    <Col md={5}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">Search Poojari</Form.Label>
                              <Form.Control
                                   type="text"
                                   placeholder="Search by name or employee code"
                                   value={searchTerm}
                                   onChange={(e) => setSearchTerm(e.target.value)}
                                   size="sm"
                              />
                         </Form.Group>
                    </Col>
                    <Col md={2}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">&nbsp;</Form.Label>
                              <Button
                                   variant="primary"
                                   onClick={fetchpoojariAttendance}
                                   disabled={loading}
                                   className="w-100"
                                   size="sm"
                              >
                                   {loading ? <Spinner size="sm" /> : 'Refresh'}
                              </Button>
                         </Form.Group>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                         <Badge bg="info" className="w-100 text-center py-2">
                              {filteredpoojaris.length} found
                         </Badge>
                    </Col>
               </Row>

               {/* Compact Data Table */}
               <Card className="shadow-sm">
                    <Card.Body className="p-0">
                         {loading ? (
                              <div className="text-center py-3">
                                   <Spinner animation="border" size="sm" />
                                   <p className="mt-2 mb-0 small">Loading...</p>
                              </div>
                         ) : (
                              <>
                                   <div className="table-responsive">
                                        <Table striped hover className="mb-0 table-sm">
                                             <thead className="table-primary">
                                                  <tr>
                                                       <th className="py-2">#</th>
                                                       <th className="py-2">Code</th>
                                                       <th className="py-2">Poojari Name</th>
                                                       <th className="py-2">Days</th>
                                                       <th className="py-2">Present</th>
                                                       <th className="py-2">Absent</th>
                                                       <th className="py-2">%</th>
                                                       <th className="py-2">Actual Hrs</th>
                                                       <th className="py-2">Consider Hrs</th>
                                                       <th className="py-2">Status</th>
                                                  </tr>
                                             </thead>
                                             <tbody>
                                                  {paginatedpoojaris.map((poojari, index) => {
                                                       const stats = calculatepoojariStats(poojari);
                                                       const globalIndex = (currentPage - 1) * recordsPerPage + index + 1;

                                                       return (
                                                            <tr key={poojari.emp_id}>
                                                                 <td className="py-1">{globalIndex}</td>
                                                                 <td className="py-1">
                                                                      <code className="small">{poojari.employee_code}</code>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <div>
                                                                           <strong className="small">{poojari.employee_name}</strong>

                                                                      </div>
                                                                 </td>
                                                                 <td className="py-1">{stats.totalDays}</td>
                                                                 <td className="py-1">
                                                                      <Badge bg="success" className="small">{stats.presentDays}</Badge>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <Badge bg="danger" className="small">{stats.absentDays}</Badge>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <Badge bg={getAttendanceColor(stats.attendancePercentage)} className="small">
                                                                           {stats.attendancePercentage}%
                                                                      </Badge>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <span className="font-monospace text-info small">
                                                                           {stats.totalActualWorkingHours}h
                                                                      </span>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <span className="font-monospace text-success small">
                                                                           {stats.totalConsideredWorkingHours}h
                                                                      </span>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <Badge
                                                                           bg={stats.attendancePercentage >= 90 ? 'success' :
                                                                                stats.attendancePercentage >= 75 ? 'warning' : 'danger'}
                                                                           className="small"
                                                                      >
                                                                           {stats.attendancePercentage >= 90 ? 'Excellent' :
                                                                                stats.attendancePercentage >= 75 ? 'Good' : 'Poor'}
                                                                      </Badge>
                                                                 </td>
                                                            </tr>
                                                       );
                                                  })}
                                             </tbody>
                                        </Table>
                                   </div>

                                   {/* Compact Pagination */}
                                   {totalPages > 1 && (
                                        <div className="d-flex justify-content-between align-items-center p-2 border-top">
                                             <span className="text-muted small">
                                                  {((currentPage - 1) * recordsPerPage) + 1}-{Math.min(currentPage * recordsPerPage, filteredpoojaris.length)} of {filteredpoojaris.length}
                                             </span>
                                             <div>
                                                  <Button
                                                       variant="outline-primary"
                                                       size="sm"
                                                       disabled={currentPage === 1}
                                                       onClick={() => setCurrentPage(currentPage - 1)}
                                                       className="me-1"
                                                  >
                                                       ‹
                                                  </Button>
                                                  <span className="mx-2 small">
                                                       {currentPage}/{totalPages}
                                                  </span>
                                                  <Button
                                                       variant="outline-primary"
                                                       size="sm"
                                                       disabled={currentPage === totalPages}
                                                       onClick={() => setCurrentPage(currentPage + 1)}
                                                  >
                                                       ›
                                                  </Button>
                                             </div>
                                        </div>
                                   )}
                              </>
                         )}
                    </Card.Body>
               </Card>
          </Container>
     );
};

export default PoojariAttendanceSummery;